import React, { useEffect, useState, useMemo } from "react";
import {
  getSubscriptions,
  getPlans,
  deletePlan,
  getSubscriptionReport,
  getSubscriptionsSandbox,
  getPlansSandbox,
  deletePlanSandbox,
} from "../../services/subscription";
import toast from "react-hot-toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import LoadingScreen from "../../components/LoadingScreen";
import Toggle from "../../components/Toggle";
import { useAuth } from "../../context/AuthContextProvider";
import AddPlanModal from "../Plans/AddPlanModal";
import DeleteConfirmationModal from "../Plans/DeleteConfirmationModal";
import { FaTrashAlt } from "react-icons/fa";
import { getBusinessDetails } from "../../services/settings";

const Subscriptions = () => {
  const [activeTab, setActiveTab] = useState("subscriptions");
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Plan Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  // Delete Modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Filter & Search states
  const [subSearch, setSubSearch] = useState("");
  const [subStatusFilter, setSubStatusFilter] = useState("");
  const [subStartDate, setSubStartDate] = useState("");
  const [subEndDate, setSubEndDate] = useState("");
  const [planSearch, setPlanSearch] = useState("");

  const { globalProfile } = useAuth();
  const [merchantId, setMerchantId] = useState("");

  const getSubscriptionsReqFun =
    globalProfile === "TEST" ? getSubscriptionsSandbox : getSubscriptions;
  const getPlansReqFun =
    globalProfile === "TEST" ? getPlansSandbox : getPlans;
  const deletePlanReqFun =
    globalProfile === "TEST" ? deletePlanSandbox : deletePlan;

  const handleCredentials = async () => {
    try {
      const res = await getBusinessDetails();
      setMerchantId(res.data.merchantId);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
        setMerchantId("");
      }
    }
  };

  useEffect(() => {
    handleCredentials();
  }, [globalProfile]);

  const copyTextToClipboard = async (text) => {
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }
    } catch (e) {
      // Fall back below
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  };

  const test = "https://test.mypayloft.com/";
  const handleCopyPlanLink = async (plan) => {
    if (!plan?.id) {
      toast.error("Missing plan or merchant id");
      return;
    }

    const url = `${test}subscribe?id=${encodeURIComponent(
      plan.publicId
    )}&merchantId=${encodeURIComponent(merchantId)}`;

    try {
      await copyTextToClipboard(url);
      toast.success("Plan link copied");
    } catch (e) {
      toast.error("Failed to copy link");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const subParams =
        activeTab === "subscriptions"
          ? {
              startDate: subStartDate || undefined,
              endDate: subEndDate || undefined,
              status: subStatusFilter || undefined,
            }
          : {};

      const [subsRes, plansRes] = await Promise.all([
        getSubscriptionsReqFun(subParams),
        getPlansReqFun(),
      ]);
      setSubscriptions(subsRes || []);
      setPlans(plansRes || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const isPartialDate =
      (subStartDate && !subEndDate) || (!subStartDate && subEndDate);

    if (isPartialDate) return;

    fetchData();
  }, [globalProfile, subStatusFilter, subStartDate, subEndDate]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const filteredSubscriptions = useMemo(() => {
    return subscriptions?.filter((sub) => {
      return sub.customerEmail
        ?.toLowerCase()
        .includes(subSearch.toLowerCase());
    });
  }, [subscriptions, subSearch]);

  const filteredPlans = useMemo(() => {
    return plans.filter((plan) =>
      plan.planName?.toLowerCase().includes(planSearch.toLowerCase())
    );
  }, [plans, planSearch]);

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const params = {
        startDate: subStartDate || undefined,
        endDate: subEndDate || undefined,
        status: subStatusFilter || undefined,
      };
      const blob = await getSubscriptionReport(params);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `subscriptions-report-${new Date().getTime()}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Report downloaded successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to export report");
    } finally {
      setExportLoading(false);
    }
  };

  const handleEditPlan = (plan) => {
    setEditData(plan);
    setIsModalOpen(true);
  };

  const handleAddPlan = () => {
    setEditData(null);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (plan) => {
    setPlanToDelete(plan);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!planToDelete) return;

    setDeleteLoading(true);
    try {
      await deletePlanReqFun(planToDelete.id);
      toast.success("Plan deleted successfully");
      setIsDeleteModalOpen(false);
      setPlanToDelete(null);
      fetchData();
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message || "Failed to delete plan");
      } else {
        toast.error("An error occurred while deleting the plan");
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading && subscriptions.length === 0 && plans.length === 0) {
    return <LoadingScreen title="Recurring Billing" />;
  }

  return (
    <div className="max-w-[1200px] mx-auto">
      {/* Header Area */}
      <div className="flex flex-col xl:flex-row items-start justify-between mb-6">
        <h1 className="text-navy-blue text-2xl font-bold">Recurring Billing</h1>
        <div className="flex sm:flex-row flex-col justify-center items-center">
          <Toggle />
        </div>
      </div>

      <div className="bg-white rounded-[12px] shadow-sm min-h-[70vh]">
        {/* Tabs Header */}
        <div className="flex border-b border-gray-100 px-6">
          <button
            onClick={() => setActiveTab("subscriptions")}
            className={`py-5 px-8 font-semibold text-sm transition-all border-b-2 ${
              activeTab === "subscriptions"
                ? "border-orange-500 text-orange-500"
                : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            Subscriptions
          </button>
          <button
            onClick={() => setActiveTab("plans")}
            className={`py-5 px-8 font-semibold text-sm transition-all border-b-2 ${
              activeTab === "plans"
                ? "border-orange-500 text-orange-500"
                : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            Plans & Configuration
          </button>
        </div>

        {/* Subscriptions Tab content */}
        {activeTab === "subscriptions" && (
          <div className="p-8">
            <div className="flex justify-between items-end mb-6">
              <h2 className="text-xl font-bold text-slate-800">
                Subscriptions
              </h2>
              {globalProfile !== "TEST" && (
                <button
                  onClick={handleExportCSV}
                  disabled={exportLoading}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-slate-700 hover:bg-gray-50 transition-all disabled:opacity-50"
                >
                  {exportLoading ? "Exporting..." : "Export CSV"}
                </button>
              )}
            </div>

            {/* Action Bar */}
            <div className="flex flex-col gap-4 mb-6 bg-slate-50 p-4 rounded-lg border border-gray-100">
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Search customer email..."
                  value={subSearch}
                  onChange={(e) => setSubSearch(e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-sky-400 transition-all"
                />
                <select
                  value={subStatusFilter}
                  onChange={(e) => setSubStatusFilter(e.target.value)}
                  className="w-[180px] px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white outline-none focus:border-sky-400"
                >
                  <option value="">All Statuses</option>
                  <option value="ACTIVE">Active</option>
                  <option value="PAUSED">Paused</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>

              <div className="flex items-center gap-4 text-sm text-slate-600">
                <span className="font-semibold whitespace-nowrap text-xs uppercase tracking-wider text-slate-400">
                  Filter Date:
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="date"
                    value={subStartDate}
                    onChange={(e) => setSubStartDate(e.target.value)}
                    className="px-3 py-1.5 border border-gray-200 rounded-md text-xs outline-none focus:border-sky-400"
                  />
                  <span className="text-slate-300">to</span>
                  <input
                    type="date"
                    value={subEndDate}
                    onChange={(e) => setSubEndDate(e.target.value)}
                    className="px-3 py-1.5 border border-gray-200 rounded-md text-xs outline-none focus:border-sky-400"
                  />
                  {(subStartDate || subEndDate) && (
                    <button
                      onClick={() => {
                        setSubStartDate("");
                        setSubEndDate("");
                      }}
                      className="text-orange-500 hover:text-orange-600 text-[10px] font-bold uppercase transition-colors ml-2"
                    >
                      Clear Dates
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="relative">
              {loading && (
                <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] flex items-center justify-center z-10 transition-all rounded-lg">
                  <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    Updating list...
                  </div>
                </div>
              )}
              <DataTable
                value={filteredSubscriptions}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 20]}
                emptyMessage="No subscriptions matching your search"
                className="p-datatable-sm"
                responsiveLayout="scroll"
              >
                <Column
                  field="customerEmail"
                  header="Customer"
                  sortable
                  body={(row) => (
                    <div className="py-2 font-semibold text-sm text-slate-700">
                      {row.customerEmail}
                    </div>
                  )}
                />
                <Column
                  field="planName"
                  header="Plan"
                  sortable
                  body={(row) => (
                    <div className="py-2 text-sm text-slate-600">
                      {row.planName}
                    </div>
                  )}
                />
                <Column
                  field="lastPaymentDate"
                  header="Last Payment"
                  sortable
                  body={(row) => (
                    <div className="py-2 text-sm text-slate-500">
                      {formatDate(row.lastPaymentDate)}
                    </div>
                  )}
                />
                <Column
                  field="nextBillingDate"
                  header="Next Due"
                  sortable
                  body={(row) => (
                    <div className="py-2 text-sm text-slate-500">
                      {formatDate(row.nextBillingDate)}
                    </div>
                  )}
                />
                <Column
                  header="Method"
                  body={(row) => (
                    <div className="flex flex-col py-2">
                      <span className="text-xs font-semibold text-slate-600">
                        {row.cardScheme}
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono tracking-tighter">
                        {row.cardMasked}
                      </span>
                    </div>
                  )}
                />
                <Column
                  field="status"
                  header="Status"
                  sortable
                  body={(row) => (
                    <span
                      className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                        row.status === "ACTIVE"
                          ? "bg-emerald-100 text-emerald-700"
                          : row.status === "PAUSED"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-rose-100 text-rose-700"
                      }`}
                    >
                      {row.status || "Inactive"}
                    </span>
                  )}
                />
              </DataTable>
            </div>
          </div>
        )}

        {/* Plans tab content */}
        {activeTab === "plans" && (
          <div className="p-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="flex justify-between items-end mb-6">
              <h2 className="text-xl font-bold text-slate-800">Billing Plans</h2>
              <button
                onClick={handleAddPlan}
                className="px-5 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-bold shadow-sm shadow-orange-200 hover:bg-orange-600 transition-all"
              >
                + Create Plan
              </button>
            </div>

            {/* Action Bar */}
            <div className="flex gap-3 mb-6 bg-slate-50 p-3 rounded-lg border border-gray-100">
              <input
                type="text"
                placeholder="Search plans by name..."
                value={planSearch}
                onChange={(e) => setPlanSearch(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-sky-400 transition-all"
              />
            </div>

            <DataTable
              value={filteredPlans}
              paginator
              rows={10}
              rowsPerPageOptions={[5, 10, 20]}
              emptyMessage="No plans found"
              className="p-datatable-sm"
              responsiveLayout="scroll"
            >
              <Column
                field="planName"
                header="Plan Name"
                sortable
                body={(row) => (
                  <div className="py-2 font-semibold text-sm text-slate-700">
                    {row.planName}
                  </div>
                )}
              />
              <Column
                field="amount"
                header="Amount"
                sortable
                body={(row) => (
                  <div className="py-2 text-sm text-slate-900 font-bold">
                    {row?.currency} {row?.amount}
                  </div>
                )}
              />
              <Column
                field="frequency"
                header="Frequency"
                sortable
                body={(row) => (
                  <div className="py-2 text-sm text-slate-600 capitalize">
                    {row.frequency?.toLowerCase()}
                  </div>
                )}
              />
              <Column
                field="maxPayments"
                header="Max Payments"
                sortable
                body={(row) => (
                  <div className="py-2 text-sm text-slate-800 font-bold">
                    {row.maxPayments || "-"}
                  </div>
                )}
              />
              <Column
                field="planCode"
                header="Plan Code"
                sortable
                body={(row) => (
                  <div className="py-2">
                    <code className="bg-slate-100 px-2 py-0.5 rounded text-[11px] text-slate-600 font-mono">
                      {row.planCode}
                    </code>
                  </div>
                )}
              />
              <Column
                field="totalSubscribers"
                header="Total Subs"
                sortable
                body={(row) => (
                  <div className="py-2 text-sm text-slate-600 text-center font-semibold">
                    {row.totalSubscribers}
                  </div>
                )}
              />
              <Column
                field="status"
                header="Status"
                sortable
                body={(row) => (
                  <span
                    className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                      row.status === "ACTIVE"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-rose-100 text-rose-700"
                    }`}
                  >
                    {row.status}
                  </span>
                )}
              />
              <Column
                header="Copy Link"
                body={(row) => (
                  <div className="text-center py-2">
                    <button
                      onClick={() => handleCopyPlanLink(row)}
                      className="px-3 py-1.5 border border-slate-200 rounded-md text-xs font-bold text-slate-700 hover:bg-slate-50 transition-all"
                      title="Copy subscribe link"
                    >
                      Copy
                    </button>
                  </div>
                )}
              />
              <Column
                header="Action"
                body={(row) => (
                  <div className="flex items-center justify-center gap-4 py-2">
                    <button
                      onClick={() => handleEditPlan(row)}
                      className="text-sky-500 text-sm font-bold hover:text-sky-600 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClick(row)}
                      className="text-rose-400 hover:text-rose-600 transition-colors"
                      title="Delete Plan"
                    >
                      <FaTrashAlt size={14} />
                    </button>
                  </div>
                )}
              />
            </DataTable>
          </div>
        )}
      </div>

      {/* Modals */}
      {isModalOpen && (
        <AddPlanModal
          setIsModalOpen={setIsModalOpen}
          onSuccess={fetchData}
          editData={editData}
        />
      )}

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Billing Plan"
        message={`Are you sure you want to delete "${planToDelete?.planName}"? This action cannot be undone and will permanently remove the plan code ${planToDelete?.planCode}.`}
        loading={deleteLoading}
      />
    </div>
  );
};

export default Subscriptions;
